import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Pencil, Plus, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { bolehKelolaSurvei, bolehLihatSurvei } from "@/lib/roles";
import { api, formatApiErrorDetail } from "@/lib/api";
import SurveiTempelMaster from "@/components/SurveiTempelMaster";
import { NAMA_MASTER_MAKS, periksaNamaMaster } from "@/lib/surveiKepnakes";

// Tab "Master Kepnakes" (PR #112; kelola khusus Super Admin, Admin UFP baca-saja; server juga menolak peran lain): daftar
// Institusi & Program Studi (+ jenjang) yg dipilih responden Kepnakes. Tambah, ubah, nonaktifkan/aktifkan, hapus (hanya bila belum dipakai jawaban).
// Nonaktif = tidak muncul di formulir publik; jawaban lama tetap.
const JENIS = [
    { jenis: "institusi", judul: "Institusi", berjenjang: false },
    { jenis: "prodi", judul: "Program Studi", berjenjang: true },
];

function DialogUbah({ item, info, jenjang, institusi, onClose, onSimpan }) {
    const [form, setForm] = useState({ nama: "", jenjang: "", institusi_id: "" });
    useEffect(() => { if (item) setForm({ nama: item.nama, jenjang: item.jenjang || "", institusi_id: item.institusi_id || "" }); }, [item]);
    const galat = periksaNamaMaster(form.nama);
    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Ubah {info.judul}</DialogTitle>
                    <DialogDescription>Nama baru langsung tampil di formulir survei Kepnakes.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label htmlFor="master-ubah-nama">Nama</Label>
                        <Input id="master-ubah-nama" data-testid="master-ubah-nama" maxLength={NAMA_MASTER_MAKS} value={form.nama}
                               onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} />
                        {galat && form.nama && <p className="text-xs text-rose-600">{galat}</p>}
                    </div>
                    {info.berjenjang && (
                        <div className="space-y-1">
                            <Label>Institusi</Label>
                            <Select value={form.institusi_id} onValueChange={(v) => setForm((f) => ({ ...f, institusi_id: v }))}>
                                <SelectTrigger data-testid="master-ubah-institusi"><SelectValue placeholder="Pilih institusi" /></SelectTrigger>
                                <SelectContent>{institusi.map((i) => <SelectItem key={i.id} value={i.id}>{i.nama}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                    {info.berjenjang && (
                        <div className="space-y-1">
                            <Label>Jenjang</Label>
                            <Select value={form.jenjang} onValueChange={(v) => setForm((f) => ({ ...f, jenjang: v }))}>
                                <SelectTrigger><SelectValue placeholder="Pilih jenjang" /></SelectTrigger>
                                <SelectContent>{jenjang.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Batal</Button>
                    <Button disabled={!!galat || (info.berjenjang && !form.institusi_id)} onClick={() => onSimpan(form)} className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="master-ubah-simpan">Simpan</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const SEMUA = "__semua";
const TANPA = "__tanpa";

function DaftarMaster({ info, daftar, jenjang, institusi, onBerubah, bacaSaja = false }) {
    const [nama, setNama] = useState("");
    const [jenjangBaru, setJenjangBaru] = useState("");
    const [ubah, setUbah] = useState(null);
    const [konfirmasi, setKonfirmasi] = useState(null); // {jenis: "hapus"|"nonaktif", item}
    const [tempel, setTempel] = useState(false);
    // Program Studi bertingkat: pemilih Institusi di atas daftar (bawaan = institusi pertama); "Semua" & "Tanpa institusi" untuk tinjauan.
    const [institusiPilih, setInstitusiPilih] = useState("");
    const idInstitusi = new Set(institusi.map((i) => i.id));
    const pilihAktif = info.berjenjang ? (institusiPilih || institusi[0]?.id || SEMUA) : SEMUA;
    const namaInstitusi = Object.fromEntries(institusi.map((i) => [i.id, i.nama]));
    const tampil = !info.berjenjang || pilihAktif === SEMUA ? daftar
        : daftar.filter((d) => (pilihAktif === TANPA ? !idInstitusi.has(d.institusi_id) : d.institusi_id === pilihAktif));
    const adaTanpaInstitusi = info.berjenjang && daftar.some((d) => !idInstitusi.has(d.institusi_id));
    const institusiTambah = info.berjenjang && idInstitusi.has(pilihAktif) ? pilihAktif : "";
    const galat = periksaNamaMaster(nama);
    const url = `/survei/master-kepnakes/${info.jenis}`;

    const jalankan = async (aksi, pesan) => {
        try { await aksi(); toast.success(pesan); onBerubah(); return true; } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail)); return false;
        }
    };
    const tambah = async (ev) => {
        ev.preventDefault();
        if (galat || (info.berjenjang && (!jenjangBaru || !institusiTambah))) return;
        const ok = await jalankan(() => api.post(url, { nama, ...(info.berjenjang ? { jenjang: jenjangBaru, institusi_id: institusiTambah } : {}) }), `${info.judul} ditambahkan`);
        if (ok) setNama("");
    };
    const simpanUbah = async (form) => {
        const ok = await jalankan(() => api.put(`${url}/${ubah.id}`, { nama: form.nama, ...(info.berjenjang ? { jenjang: form.jenjang, institusi_id: form.institusi_id } : {}) }), "Perubahan disimpan");
        if (ok) setUbah(null);
    };
    const jalankanKonfirmasi = async () => {
        const { jenis, item } = konfirmasi;
        if (jenis === "hapus") await jalankan(() => api.delete(`${url}/${item.id}`), `${info.judul} dihapus`);
        else await jalankan(() => api.put(`${url}/${item.id}`, { aktif: false }), `${info.judul} dinonaktifkan`);
        setKonfirmasi(null);
    };

    return (
        <Card className="overflow-hidden" data-testid={`master-${info.jenis}`}>
            {info.berjenjang && (
                <div className="flex flex-wrap items-end gap-3 border-b bg-slate-50 p-4 dark:bg-slate-900">
                    <div className="w-full max-w-sm space-y-1">
                        <Label>Tampilkan program studi milik institusi</Label>
                        <Select value={pilihAktif} onValueChange={setInstitusiPilih}>
                            <SelectTrigger data-testid="master-prodi-filter-institusi"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {institusi.map((i) => <SelectItem key={i.id} value={i.id}>{i.nama} ({i.jumlah_prodi})</SelectItem>)}
                                <SelectItem value={SEMUA}>Semua institusi</SelectItem>
                                {adaTanpaInstitusi && <SelectItem value={TANPA}>Tanpa institusi</SelectItem>}
                            </SelectContent>
                        </Select>
                    </div>
                    {!bacaSaja && !institusiTambah && <p className="text-xs text-slate-600" data-testid="master-prodi-pilih-institusi">Pilih satu institusi untuk menambah program studi.</p>}
                </div>
            )}
            {!bacaSaja && <form onSubmit={tambah} className="flex flex-wrap items-end gap-3 border-b p-4">
                <div className="min-w-[14rem] flex-1 space-y-1">
                    <Label htmlFor={`master-${info.jenis}-nama`}>{info.judul} baru</Label>
                    <Input id={`master-${info.jenis}-nama`} data-testid={`master-${info.jenis}-nama`} maxLength={NAMA_MASTER_MAKS}
                           value={nama} onChange={(e) => setNama(e.target.value)} placeholder={`Nama ${info.judul.toLowerCase()}`} />
                </div>
                {info.berjenjang && (
                    <div className="w-64 space-y-1">
                        <Label>Jenjang</Label>
                        <Select value={jenjangBaru} onValueChange={setJenjangBaru}>
                            <SelectTrigger data-testid={`master-${info.jenis}-jenjang`}><SelectValue placeholder="Pilih jenjang" /></SelectTrigger>
                            <SelectContent>{jenjang.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                )}
                <Button type="submit" disabled={!!galat || (info.berjenjang && (!jenjangBaru || !institusiTambah))} className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid={`master-${info.jenis}-tambah`}>
                    <Plus className="mr-2 h-4 w-4" /> Tambah
                </Button>
                <Button type="button" variant="outline" onClick={() => setTempel(true)} data-testid={`master-${info.jenis}-tempel`}>
                    <ClipboardPaste className="mr-2 h-4 w-4" /> Tempel dari Excel
                </Button>
            </form>}
            <div className="overflow-x-auto">
                <table className="compact w-full" data-testid={`master-${info.jenis}-tabel`}>
                    <thead className="bg-slate-100">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                            <th>Nama</th>{info.berjenjang && <th>Institusi</th>}{info.berjenjang && <th>Jenjang</th>}{!info.berjenjang && <th>Program studi</th>}<th>Status</th>{!bacaSaja && <th className="pr-4 text-right">Aksi</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {tampil.length === 0 ? (
                            <tr><td colSpan={(info.berjenjang ? 5 : 4) - (bacaSaja ? 1 : 0)} className="py-8 text-center text-slate-400">Belum ada data.</td></tr>
                        ) : tampil.map((d) => (
                            <tr key={d.id} className="border-t border-slate-200" data-testid={`master-baris-${d.id}`}>
                                <td className="font-semibold text-slate-800">{d.nama}</td>
                                {info.berjenjang && (
                                    <td className="text-xs" data-testid={`master-institusi-${d.id}`}>
                                        {d.tanpa_institusi ? <Badge variant="outline" className="border-amber-500 text-amber-700">Tanpa institusi</Badge> : namaInstitusi[d.institusi_id]}
                                    </td>
                                )}
                                {info.berjenjang && <td className="text-xs">{d.jenjang}</td>}
                                {!info.berjenjang && <td className="text-xs tabular-nums" data-testid={`master-jumlah-prodi-${d.id}`}>{d.jumlah_prodi ?? 0}</td>}
                                <td>{d.aktif ? <Badge className="bg-emerald-600 text-white">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}</td>
                                {!bacaSaja && <td className="whitespace-nowrap pr-2 text-right">
                                    {d.aktif ? (
                                        <Button size="sm" variant="outline" onClick={() => setKonfirmasi({ jenis: "nonaktif", item: d })} data-testid={`master-nonaktif-${d.id}`}>Nonaktifkan</Button>
                                    ) : (
                                        <Button size="sm" variant="outline" onClick={() => jalankan(() => api.put(`${url}/${d.id}`, { aktif: true }), `${info.judul} diaktifkan`)} data-testid={`master-aktif-${d.id}`}>Aktifkan</Button>
                                    )}
                                    <Button size="sm" variant="ghost" aria-label={`Ubah ${d.nama}`} onClick={() => setUbah(d)} data-testid={`master-ubah-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                                    {!d.dipakai && !(d.jumlah_prodi > 0) && (
                                        <Button size="sm" variant="ghost" aria-label={`Hapus ${d.nama}`} className="text-rose-600" onClick={() => setKonfirmasi({ jenis: "hapus", item: d })} data-testid={`master-hapus-${d.id}`}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!bacaSaja && <SurveiTempelMaster open={tempel} info={info} jenjang={jenjang} institusi={institusi} institusiAwal={institusiTambah} onTutup={() => setTempel(false)} onSelesai={() => onBerubah()} />}
            {!bacaSaja && <DialogUbah item={ubah} info={info} jenjang={jenjang} institusi={institusi} onClose={() => setUbah(null)} onSimpan={simpanUbah} />}
            <AlertDialog open={!!konfirmasi} onOpenChange={(o) => !o && setKonfirmasi(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{konfirmasi?.jenis === "hapus" ? `Hapus ${info.judul.toLowerCase()}?` : `Nonaktifkan ${info.judul.toLowerCase()}?`}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {konfirmasi?.jenis === "hapus"
                                ? `"${konfirmasi.item.nama}" belum dipakai jawaban dan akan dihapus permanen.`
                                : `"${konfirmasi?.item.nama}" tidak lagi muncul di formulir survei. Jawaban yang sudah masuk tetap tersimpan.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={jalankanKonfirmasi} data-testid="master-konfirmasi-ya">Ya, lanjutkan</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

export default function SurveiMasterKepnakes() {
    const { user } = useAuth();
    const boleh = bolehLihatSurvei(user?.role);
    const kelola = bolehKelolaSurvei(user?.role);
    const [data, setData] = useState(null);
    const muat = useCallback(() => api.get("/survei/master-kepnakes")
        .then((r) => setData(r.data))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), []);
    useEffect(() => { if (boleh) muat(); }, [boleh, muat]);

    if (!boleh) return null;
    if (!data) return <p className="py-6 text-sm text-slate-500">Memuat…</p>;
    return (
        <div className="space-y-6" data-testid="survei-master-kepnakes">
            <p className="text-sm text-slate-600">
                Daftar pilihan untuk responden survei Kepnakes (keperawatan dan tenaga kesehatan dari institusi pendidikan luar).
                {kelola && "Data yang sudah dipakai jawaban tidak bisa dihapus; nonaktifkan saja."}
            </p>
            {JENIS.map((info) => (
                <section key={info.jenis} className="space-y-2">
                    <h2 className="text-lg font-bold text-slate-900" data-testid={`master-judul-${info.jenis}`}>{info.judul} ({(data[info.jenis] || []).length})</h2>
                    <DaftarMaster info={info} daftar={data[info.jenis] || []} jenjang={data.jenjang || []} institusi={data.institusi || []} onBerubah={muat} bacaSaja={!kelola} />
                </section>
            ))}
        </div>
    );
}
